import type { Modal } from '@ps/web-core-store';
import { Button } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { PriorityImage } from '@swf/poker';

import DefaultImage from '../../assets/default-ticket.jpg';
import Calendar from '../../assets/icons/calendar.svg';
import ExternalLink from '../../assets/icons/external-link.svg';
import useFormatEventDate from '../../hooks/useFormatEventDate';
import type { Pass } from '../../types';
import { ModalRoutes } from '../../types';

import { TXTCLI_Event_Info, TXTCLI_Redeem_3, TXTCLI_See_Bundle } from './Card.messages.json';
import styles from './Card.scss';

const Card = ({ name, image, awards, value, event, type, tournId, redemptionType }: Pass) => {
  const { modal } = useDispatch<Dispatch>();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const { formatMessage } = useIntl();
  const eventInfoMessage = formatMessage(TXTCLI_Event_Info);

  const { startDate, endDate, infoUrl, type: eventType } = event;
  const formattedInfoLink = isEmbedded ? `external-${infoUrl}` : infoUrl;
  const formattedEventDate = useFormatEventDate({ startDate, endDate, type: eventType });

  const handleOpenRedeemModal = () => {
    const locationParams: Modal = {
      path: ModalRoutes.RedeemPassWizard,
      locationState: {
        name,
        formattedEventDate,
        image,
        type,
        tournId,
        redemptionType
      }
    };

    modal.enqueue(isAuthenticated ? locationParams : { path: '/auth/login' });
  };

  const handleOpenBundle = () =>
    modal.enqueue({
      path: ModalRoutes.PassBundle,
      locationState: { passAwards: awards, type, name, value, redemptionType }
    });

  return (
    <div className={styles.card}>
      <div className={styles.eventInfoWrapper}>
        <StarsLink target="_blank" href={formattedInfoLink} className={styles.cardButtonLink}>
          <span className={styles.eventInfoText}>{eventInfoMessage}</span>
          <ExternalLink width={20} height={20} />
        </StarsLink>
      </div>
      <div className={styles.cardImageWrapper}>
        <PriorityImage
          imageUrl={image}
          fallbackUrl={DefaultImage}
          altText={name}
          fetchPriority="high"
          className={styles.cardImage}
        />
      </div>
      <div>
        <div
          className={classNames(styles.cardTitleWrapper, styles[`${type}` as keyof typeof styles])}
        >
          <div className={styles.cardTitle}>{name}</div>
        </div>
        <div className={styles.cardInformation}>
          <div className={styles.cardEventDateContainer}>
            <span>
              <Calendar width={12} height={12} />
            </span>
            <span className={styles.cardDate}>{formattedEventDate}</span>
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              width="full"
              size="small"
              variant="secondary"
              translation={TXTCLI_See_Bundle}
              trackText={`${TXTCLI_See_Bundle.defaultMessage}: ${name}`}
              onClick={handleOpenBundle}
            />
            <Button
              width="full"
              size="small"
              variant="secondary"
              translation={TXTCLI_Redeem_3}
              trackText={`${TXTCLI_Redeem_3.defaultMessage}: ${name}`}
              onClick={handleOpenRedeemModal}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
